import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Compass, FolderKanban, CalendarDays, Lightbulb, Home } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useBrands } from "@/hooks/use-brands";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export function NotFoundPage() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { data: brands, isLoading: brandsLoading } = useBrands();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/login", { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  if (authLoading || !isAuthenticated || brandsLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Skeleton className="h-6 w-48" />
      </div>
    );
  }

  const firstBrand = brands?.[0];
  const prefix = firstBrand ? `/${firstBrand.slug}` : "";

  const links = firstBrand
    ? [
        { to: `${prefix}`, labelKey: "Projects", icon: FolderKanban },
        { to: `${prefix}/calendar`, labelKey: "Calendar", icon: CalendarDays },
        { to: `${prefix}/ideas`, labelKey: "Ideas", icon: Lightbulb },
        { to: "/dashboard", labelKey: "Dashboard", icon: Home },
      ]
    : [{ to: "/dashboard", labelKey: "Dashboard", icon: Home }];

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center px-6 py-12">
      <div className="w-full max-w-md text-center space-y-6">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-brand-subtle text-brand-subtle-text">
          <Compass size={32} className="animate-pulse" />
        </div>

        <div className="space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            {t("notFound.errorCode")}
          </p>
          <h1 className="text-3xl font-bold tracking-tight">{t("notFound.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("notFound.message")}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2 pt-2">
          {links.map(({ to, labelKey, icon: Icon }) => (
            <Button
              key={to}
              asChild
              variant="outline"
              className="h-auto justify-start gap-2 py-2.5"
            >
              <Link to={to}>
                <Icon size={14} />
                <span className="text-[13px]">{t(`notFound.links.${labelKey}`)}</span>
              </Link>
            </Button>
          ))}
        </div>

        <p className="text-[11px] text-muted-foreground pt-2">
          {t("notFound.goBack")}
        </p>
      </div>
    </div>
  );
}
